const PREPAYMENT_ENDPOINT = '/core/Prepayment/SendToViaduct';

const enum PrepaymentStatus {
    Skipped = 0,
    Ok = 1,
    Failed = 2,
}

type NumericLike = string | number | null | undefined;

interface SourceNomenclature {
    Number?: string | null;
    IsProduct?: boolean | null;
    LeftRight?: string | null;
    OrderHasProcedure_UID?: string | number | null;
    ProcedureCode?: string | null;
    RoomMachineNumber?: string | null;
    ParentNumber?: string | null;
    ProductCode?: string | null;    
}

interface InvoiceModel {
    OnDuty?: boolean | null;
    OnDutyLevel?: string | null;
    ProcedureAndProductNomenclatures?: SourceNomenclature[] | null;
    DateOfImplementationString?: string | null;
    DateOfImplementation?: string | null;
    SiteCode?: string | null;
}

interface PrepaymentNomenclaturePayload {
    nomenclatureNumber: string;
    type: 'product' | 'prestation';
    radiologistId: number;
    laterality: string;
    roomMachineNumber: string;
    parentNomenclature: string;
    quantity: number;
    onDuty: string;
    productCode: string;
}

interface PrepaymentPayload {
    visitNr: string;
    patientId: string;
    studyDate: string;
    prescriptionDate: string;
    applicantId: number;
    identifier: string;
    orderId: number;
    siteCode: string;
    nomenclatures: PrepaymentNomenclaturePayload[];
}

interface PrepaymentApiResult {
    Status?: number;
    Amount?: number | string | null;
    Error?: string | null;
}

interface PrepaymentAmountResult {
    amount: number | null;
    error: string | null;
}

interface RadiologistLookups {
    byOhpId: Record<number, number>;
    byProcedureCode: Record<string, number>;
}

declare global {
    var modelOrderNumber: string | undefined;
}

export function fetchPrepaymentAmount(uniquePrefix: string, orderId: NumericLike): Promise<PrepaymentAmountResult> {
    const payload = buildPrepaymentPayload(uniquePrefix, orderId);
    if (!payload) {
        return Promise.resolve({ amount: null, error: null });
    }

    return fetch(PREPAYMENT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    })
        .then((response) => response.json() as Promise<PrepaymentApiResult>)
        .then((result) => {
            if (!result) {
                return { amount: null, error: null };
            }
            if (result.Error) {
                return { amount: null, error: String(result.Error) };
            }
            if (result.Status === PrepaymentStatus.Ok && result.Amount != null) {
                return { amount: Number.parseFloat(String(result.Amount)), error: null };
            }
            if (result.Status === PrepaymentStatus.Failed) {
                return { amount: null, error: result.Error ?? 'Prepayment failed' };
            }
            return { amount: null, error: null };
        })
        .catch(() => ({ amount: null, error: null }));
}

function buildPrepaymentPayload(uniquePrefix: string, orderId: NumericLike): PrepaymentPayload | null {
    const raw = sessionStorage.getItem(`OrderNomenclatureGridInvoiceModel${uniquePrefix}`);
    if (!raw) {
        return null;
    }

    let model: InvoiceModel;
    try {
        model = JSON.parse(raw) as InvoiceModel;
    }
    catch {
        return null;
    }

    if (model == null || typeof model !== 'object') {
        return null;
    }

    const onDutyLevel = model.OnDuty && model.OnDutyLevel != null ? String(model.OnDutyLevel).toLowerCase() : '';
    const radiologistLookups = getRadiologistLookups();
    const nomenclatures = mapSourceNomenclatures(model.ProcedureAndProductNomenclatures ?? [], onDutyLevel, radiologistLookups);

    const visitNr = getInputValueById('activeVisitNumber');
    const orderNumber = globalThis.modelOrderNumber ?? '';
    const applicantId = parseIntOrZero(getInputValueById('Applicant_UID'));

    return {
        visitNr: visitNr ? String(visitNr) : '',
        patientId: getInputValueBySelector('input#PatientModel_InternalNumber').trim(),
        studyDate: formatBillingDate(model.DateOfImplementationString ?? model.DateOfImplementation),
        prescriptionDate: formatBillingDate(getInputValueBySelector('input#DateOfPrescription')),
        applicantId,
        identifier: orderNumber,
        orderId: parseIntOrZero(orderId),
        nomenclatures,
        siteCode: model.SiteCode ? String(model.SiteCode) : '',
    };
}

function mapSourceNomenclatures(
    sourceNomenclatures: SourceNomenclature[],
    onDutyLevel: string,
    radiologistLookups: RadiologistLookups,
): PrepaymentNomenclaturePayload[] {
    const items = sourceNomenclatures.filter(hasNomenclatureNumber);
    const productQuantities = countProductQuantities(items);
    const seenProducts: Record<string, boolean> = {};
    const nomenclatures: PrepaymentNomenclaturePayload[] = [];

    items.forEach((item) => {
        const isProduct = !!item.IsProduct;
        if (isProduct) {
            const key = getProductDedupeKey(item);
            if (seenProducts[key]) {
                return;
            }
            seenProducts[key] = true;
        }

        const quantity = isProduct ? productQuantities[getProductDedupeKey(item)] : 1;
        nomenclatures.push(...mapNomenclaturesForBilling(item, isProduct, onDutyLevel, quantity, radiologistLookups));
    });

    return nomenclatures;
}

function hasNomenclatureNumber(item: SourceNomenclature): boolean {
    return !!item.Number?.trim();
}

function countProductQuantities(items: SourceNomenclature[]): Record<string, number> {
    const quantities: Record<string, number> = {};
    items.forEach((item) => {
        if (!item.IsProduct) {
            return;
        }
        const key = getProductDedupeKey(item);
        quantities[key] = (quantities[key] ?? 0) + 1;
    });
    return quantities;
}

// InvoiceModel LeftRight: L / R / B (both) / I (indifferent).
// Viaduct expects L or R only. For "both", send the nomenclature twice (once L, once R).
function mapNomenclaturesForBilling(
    item: SourceNomenclature,
    isProduct: boolean,
    onDutyLevel: string,
    quantity: number,
    radiologistLookups: RadiologistLookups,
): PrepaymentNomenclaturePayload[] {
    const leftRight = item.LeftRight == null ? '' : String(item.LeftRight).trim().toUpperCase();
    if (leftRight === 'B') {
        return [
            mapNomenclature(item, isProduct, onDutyLevel, 'L', quantity, radiologistLookups),
            mapNomenclature(item, isProduct, onDutyLevel, 'R', quantity, radiologistLookups),
        ];
    }
    if (leftRight === 'L' || leftRight === 'R') {
        return [mapNomenclature(item, isProduct, onDutyLevel, leftRight, quantity, radiologistLookups)];
    }

    return [mapNomenclature(item, isProduct, onDutyLevel, '', quantity, radiologistLookups)];
}

function formatBillingDate(value: string | null | undefined): string {
    if (!value) {
        return '';
    }
    const trimmedValue = value.trim();

    // DD/MM/YYYY -> YYYYMMDD
    const ddMmYyyyMatch = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(trimmedValue);
    if (ddMmYyyyMatch) {
        const [, day, month, year] = ddMmYyyyMatch;
        return `${year}${month}${day}`;
    }

    // YYYY-MM-DD -> YYYYMMDD
    const isoDateMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmedValue);
    if (isoDateMatch) {
        const [, year, month, day] = isoDateMatch;
        return `${year}${month}${day}`;
    }

    return '';
}

function mapNomenclature(
    item: SourceNomenclature,
    isProduct: boolean,
    onDutyLevel: string,
    laterality: string,
    quantity: number,
    radiologistLookups: RadiologistLookups,
): PrepaymentNomenclaturePayload {
    const orderHasProcedureId = parseIntOrZero(item.OrderHasProcedure_UID);
    return {
        nomenclatureNumber: item.Number ?? '',
        type: isProduct ? 'product' : 'prestation',
        radiologistId: radiologistLookups.byOhpId[orderHasProcedureId]
            ?? radiologistLookups.byProcedureCode[item.ProcedureCode ?? '']
            ?? 0,
        laterality,
        roomMachineNumber: item.RoomMachineNumber ?? '',
        parentNomenclature: item.ParentNumber ?? '',
        quantity,
        onDuty: onDutyLevel,
        productCode: isProduct ? (item.ProductCode ?? '') : '',
    };
}

function getRadiologistLookups(): RadiologistLookups {
    const byOhpId: Record<number, number> = {};
    const byProcedureCode: Record<string, number> = {};

    const selectedRows = document.querySelectorAll<HTMLTableRowElement>(
        'table#selectedProcedures tr.selectedProcedureItem',
    );

    selectedRows.forEach((row) => {
        const ohpId = parseIntOrZero(row.querySelector<HTMLInputElement>('input.uid')?.value);
        const radiologistId = parseIntOrZero(row.querySelector<HTMLInputElement>('input.radUid')?.value);
        const code = (row.querySelector<HTMLInputElement>('input.code')?.value ?? '').trim();
        if (radiologistId) {
            if (ohpId) {
                byOhpId[ohpId] = radiologistId;
            }
            if (code) {
                byProcedureCode[code] = radiologistId;
            }
        }
    });

    return { byOhpId, byProcedureCode };
}

function getProductDedupeKey(item: SourceNomenclature): string {
    return `${item.ProductCode ?? ''}|${item.Number ?? ''}`;
}

function parseIntOrZero(value: NumericLike): number {
    const parsedValue = Number.parseInt(String(value), 10);
    return Number.isNaN(parsedValue) ? 0 : parsedValue;
}

function getInputValueById(id: string): string {
    return (document.getElementById(id) as HTMLInputElement | null)?.value ?? '';
}

function getInputValueBySelector(selector: string): string {
    return document.querySelector<HTMLInputElement>(selector)?.value ?? '';
}
